// A contribution is a song: up to three 4-bar patterns (A in `events`, B–C in `extra_patterns`)
// arranged into four sections (`arrangement`, default A A A A). Mirrors `LoopData` helpers in
// `engine/src/song.rs` (pattern_count, sections, song_events).

import { LOOP_TICKS, MAX_PATTERNS, SECTIONS } from "./clock";
import type { LoopData } from "./generated/LoopData";
import type { NoteEvent } from "./generated/NoteEvent";

export const PATTERN_NAMES = ["A", "B", "C"] as const;

/** How many patterns the song has (1–4). */
export function patternCount(l: Pick<LoopData, "extra_patterns">): number {
  return 1 + Math.min(l.extra_patterns?.length ?? 0, MAX_PATTERNS - 1);
}

/** Events of pattern `i` (0 = A); empty for a missing pattern. */
export function patternEvents(l: Pick<LoopData, "events" | "extra_patterns">, i: number): NoteEvent[] {
  if (i === 0) return l.events;
  if (i < patternCount(l)) return l.extra_patterns![i - 1];
  return [];
}

/** Every pattern's events, A first. */
export function allPatterns(l: Pick<LoopData, "events" | "extra_patterns">): NoteEvent[][] {
  return Array.from({ length: patternCount(l) }, (_, i) => patternEvents(l, i));
}

/** The pattern each of the four sections plays (invalid entries play A). */
export function sections(l: Pick<LoopData, "extra_patterns" | "arrangement">): number[] {
  const n = patternCount(l);
  return Array.from({ length: SECTIONS }, (_, k) => {
    const p = l.arrangement?.[k] ?? 0;
    return Number.isInteger(p) && p >= 0 && p < n ? p : 0;
  });
}

/** The whole 16-bar song: each section's pattern at its offset (ticks 0 … 61 439). */
export function songEvents(l: Pick<LoopData, "events" | "extra_patterns" | "arrangement">): NoteEvent[] {
  const out: NoteEvent[] = [];
  sections(l).forEach((p, k) => {
    for (const e of patternEvents(l, p)) out.push({ ...e, tick: e.tick + k * LOOP_TICKS });
  });
  return out.sort((a, b) => a.tick - b.tick || a.pad - b.pad || a.note - b.note);
}

/** Every note of every pattern (each pattern once), e.g. for marking or "is it empty?". */
export function everyEvent(l: Pick<LoopData, "events" | "extra_patterns">): NoteEvent[] {
  return allPatterns(l).flat();
}

/** A copy of the song with pattern `i`'s events replaced. */
export function withPattern(l: LoopData, i: number, events: NoteEvent[]): LoopData {
  if (i === 0) return { ...l, events };
  const extra = [...(l.extra_patterns ?? [])];
  while (extra.length < i) extra.push([]);
  extra[i - 1] = events;
  return { ...l, extra_patterns: extra };
}

/** Is the arrangement the default (one pattern, A A A A)? */
export function isPlainLoop(l: Pick<LoopData, "extra_patterns" | "arrangement">): boolean {
  return patternCount(l) === 1 && sections(l).every((p) => p === 0);
}
